import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Button } from '@ui/components/ui/button';
import { Language } from '@app/constants/enums';
import { formatTimestamp, getFullNameOrEmail } from '@app/utils';
import { SprintProductEntity } from '@data';
import ExpandableContent from '@app/components/shared/expandableContent';
import { Check, MessageSquareQuote } from 'lucide-react';
import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UnreadCommentDot } from './UnreadCommentDot';

/**
 * "Who decided, and when", as one line. Every place that states this fact goes through here, so the
 * client's report header, the PM's, and the dialog itself can't word it three different ways — which is
 * exactly what happened when two of them formatted their own.
 */
const useDecisionBy = (
  person?: SprintProductEntity['approvedBy'],
  date?: SprintProductEntity['approvedAt'],
): string => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Optional so callers holding a report that may not exist yet can call this unconditionally; they
  // only render the result once there is a decision to describe.
  if (!person) return '';

  // Day and month only: these sit inline beside a title, and the sprint's own range gives the year.
  return t('approvedByOn', {
    name: getFullNameOrEmail(person),
    date: formatTimestamp(date, language as Language, 'dd MMM'),
  });
};

/** Who approved, or that nobody did: an automatic approval says so instead of naming a person. */
export const useApprovedBy = (sprintProduct?: SprintProductEntity): string => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const byPerson = useDecisionBy(sprintProduct?.approvedBy, sprintProduct?.approvedAt);

  if (!sprintProduct?.autoApproved) return byPerson;

  return t('approvedAutomaticallyOn', {
    date: formatTimestamp(sprintProduct.approvedAt, language as Language, 'dd MMM'),
  });
};

export const useDisputedBy = (sprintProduct?: SprintProductEntity): string =>
  useDecisionBy(sprintProduct?.disputedBy, sprintProduct?.disputedAt);

/** Who settled a remark, and when: the dismissal, the change we closed with, or the client's approval. */
export const useRemarkAnsweredBy = (sprintProduct?: SprintProductEntity): string =>
  useDecisionBy(sprintProduct?.disputeResolvedBy, sprintProduct?.disputeResolvedAt);

// One labelled line of the "sent mail" header. Labels share a fixed column so the values line up.
const MetaRow = ({ label, value }: { label: string; value: string }): ReactElement => (
  <div className="flex gap-3 text-sm">
    <span className="w-28 shrink-0 text-muted-foreground">{label}</span>
    <span className="min-w-0 break-words text-foreground">{value}</span>
  </div>
);

/**
 * A decision on the report, read back as the message it was: who made it, who they forwarded it to, and
 * what they said.
 *
 * Shown to both sides, so the copy names the person rather than addressing them: on the PM's report this
 * is the client's message, not the reader's.
 */
const DecisionSummaryDialog = ({
  title,
  fromLabel,
  from,
  recipients,
  message,
  onClose,
}: {
  title: string;
  fromLabel: string;
  from: string;
  recipients: string[];
  /** What was written with the decision, when there is anything: approving writes nothing. */
  message?: { label: string; body: string };
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {/* A mail header, not a data table: who and when share one line, so the details stay a caption
            above the message rather than competing with it. Only what the client themselves chose is
            here — who the report came from isn't their doing and doesn't belong in their own message. */}
        <div className="flex flex-col gap-2">
          <MetaRow label={fromLabel} value={from} />
          {recipients.length > 0 && <MetaRow label={t('approvalSummaryForwardedTo')} value={recipients.join(', ')} />}
        </div>

        {/* What the client actually wrote, so it gets the room: a floor on the body keeps it the largest
            thing here even when it is one word. */}
        {message && (
          <div className="flex flex-col gap-2 border-t border-border pt-4">
            <span className="text-sm font-medium text-foreground">{message.label}</span>
            <div className="min-h-32 rounded-lg border border-border bg-background p-4 text-sm text-muted-foreground">
              <ExpandableContent content={message.body} />
            </div>
          </div>
        )}
        {/* No footer: a decision is read here, never undone, so the header's X (plus Escape) is the only
            way out this panel needs. */}
      </DialogContent>
    </Dialog>
  );
};

export const ApprovalSummaryDialog = ({
  sprintProduct,
  onClose,
}: {
  sprintProduct: SprintProductEntity;
  onClose: () => void;
}): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const approvedBy = useApprovedBy(sprintProduct);
  const date = formatTimestamp(sprintProduct.approvedAt, language as Language, 'dd MMM');

  return (
    <DecisionSummaryDialog
      title={t('approvalSummaryTitle')}
      fromLabel={sprintProduct.autoApproved ? t('approvalSummaryAutomaticFrom') : t('approvalSummaryFrom')}
      from={sprintProduct.autoApproved ? t('approvalSummaryAutomatic', { date }) : approvedBy}
      // "Forwarded to" echoes the copy that went out with the approval.
      recipients={sprintProduct.approvalRecipients ?? []}
      // No message at all: approving is a decision, not a note. `comment` belongs to a dispute now, and
      // the older approvals that still carry one are not worth a box that can never be filled again.
      onClose={onClose}
    />
  );
};

/** The same panel for the other decision: the remark that was made, and who it reached. */
export const DisputeSummaryDialog = ({
  sprintProduct,
  onClose,
}: {
  sprintProduct: SprintProductEntity;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const disputedBy = useDisputedBy(sprintProduct);

  return (
    <DecisionSummaryDialog
      title={t('disputeSummaryTitle')}
      fromLabel={t('disputeSummaryFrom')}
      from={disputedBy}
      recipients={sprintProduct.disputeRecipients ?? []}
      message={sprintProduct.comment ? { label: t('disputeRemarkLabel'), body: sprintProduct.comment } : undefined}
      onClose={onClose}
    />
  );
};

/**
 * The "Approved" button and the approval it opens, as one unit. Both the client's report header and the
 * PM's render this, so the two sides are the same control rather than two look-alikes that drift.
 *
 * Outline keeps it from competing with a primary action. The green sits on the tick alone, not the
 * label: while a comment is unread the tick gives way to a red dot, and a green word beside a red dot
 * read as two instructions at once — settled, and something is wrong. One accent at a time instead.
 */
export const ApprovalSummaryButton = ({
  sprintProduct,
  unread = false,
  onOpen,
}: {
  sprintProduct: SprintProductEntity;
  /** A client left a comment nobody on our side has read yet: the tick gives way to a red dot. */
  unread?: boolean;
  /** Called the first time the approval is opened, which is what counts as having read it. */
  onOpen?: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const openApproval = (): void => {
    setOpen(true);
    if (unread) onOpen?.();
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={openApproval}
        aria-haspopup="dialog"
        className="inline-flex items-center gap-1.5"
      >
        {/* The dot stands in for the tick while a comment is unread, so the tick ends up meaning both
            "approved" and "and you have seen what they said". Same 16px footprint either way, so the
            label doesn't shift when it clears. */}
        <span className="flex size-4 items-center justify-center">
          {unread ? <UnreadCommentDot /> : <Check className="size-4 text-green-700 dark:text-green-400" />}
        </span>
        {t('actions.approved')}
      </Button>
      {open && <ApprovalSummaryDialog sprintProduct={sprintProduct} onClose={() => setOpen(false)} />}
    </>
  );
};

/**
 * The same unit for a dispute: the button says one was made, and opens the remark it was made of.
 *
 * This is also the only way to clear the red dot on a disputed report. Nothing else on a closed report
 * shows what the client wrote, so without this the badge counts a remark with nowhere to go and read it.
 */
export const DisputeSummaryButton = ({
  sprintProduct,
  unread = false,
  onOpen,
}: {
  sprintProduct: SprintProductEntity;
  /** The remark has not been read on our side yet: the speech mark gives way to a red dot. */
  unread?: boolean;
  /** Called the first time the remark is opened, which is what counts as having read it. */
  onOpen?: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const openDispute = (): void => {
    setOpen(true);
    if (unread) onOpen?.();
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={openDispute}
        aria-haspopup="dialog"
        className="inline-flex items-center gap-1.5"
      >
        {/* A quote mark rather than a plain bubble: this opens what the client said, in their words, the
            way the tick opens their approval. Same 16px footprint either way, so the label doesn't shift
            when the dot clears, and no colour on it — a dispute is a message to answer, not a failure. */}
        <span className="flex size-4 items-center justify-center">
          {unread ? <UnreadCommentDot /> : <MessageSquareQuote className="size-4 text-muted-foreground" />}
        </span>
        {t('actions.disputed')}
      </Button>
      {open && <DisputeSummaryDialog sprintProduct={sprintProduct} onClose={() => setOpen(false)} />}
    </>
  );
};
